import React, { useCallback, useEffect, useState } from 'react';
import ReportsMenuBar, { ReportMenuBarSelection } from 'src/pages/client/reports/components/reports-menu-bar';
import MetricUniversalDisplayCard from 'src/pages/reports/components/metric-universal-display/metric-universal-display-card';
import { useGetAppDataMutation, useGetBreakdownDataMutation, useGetDomainDataMutation, useGetGeoDataMutation, useGetHourlyDataMutation, useGetReportMetricsMutation, useGetTimeSeriesDataMutation } from 'src/redux/admin/reporting/reporting.endpoints';
import { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import handleReportDownload from 'src/pages/reports/utils/download-report.utils';
import { Button } from 'src/components/ui/button';
import { ArchiveRestore } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ZipcodeHeatmapDisplay from '../components/metric-zipcode-display/ZipcodeHeatmapDisplay';
import { ZipcodeMetrics } from '../components/metric-zipcode-display/types';
import TimeOfDayDisplay from '../components/metric-time-of-day-display/time-of-day-display';
import { HourlyMetrics } from '../components/metric-time-of-day-display/types';
import transformHourlyData from '../utils/hourly-data.utils';
import MetricSourceRank, { MetricRankDisplayData } from '../components/metric-source-display/metric-rank-display';
import MetricPercentDisplay from '../components/metric-percent-chart/metric-percent-chart';
import { calculateRate, calculateRateDifference } from '../utils/calculated-values.utils';
import MetricBreakdownTable from '../components/metric-breakdown-table/metric-breakdown-table';
import { ReduxState } from 'src/redux/redux-state';
import { useSelector } from 'react-redux';
import { ClientView } from 'src/redux/organization/organization.types';
import { FALLBACK_DEFAULT_CLIENT } from 'src/redux/organization/organization.types';
import ReportComponentWrapper from 'src/pages/client-management/components/manage-clients/customize-view/report-component-wrapper';
import { ReportViewCustomizeContextType, useReportViewCustomize } from 'src/pages/client-management/components/manage-clients/manage-client-views';
import { handleComponentDefaultMetricSelection, handleMetricUniversalDisplayDefaultSelection } from 'src/pages/client-management/utils/client-management-utils';
import { useLiveClientViews } from 'src/hooks/use-live-client-views';

interface ReportsTabProps {
  showOldVersionButton: boolean;
  onSelectionChange?: (selection: ReportMenuBarSelection) => void;
}

const ReportsTab: React.FC<ReportsTabProps> = ({ showOldVersionButton, onSelectionChange = () => {} }) => {
  const userState = useSelector((state: ReduxState) => state.userState);
  const urlParams = new URLSearchParams(location.search);
  const previewClientId = (() => {
    const raw = urlParams.get('previewClientId');
    if (!raw || raw === 'null' || raw === 'undefined') return null;
    return raw;
  })();
  const isClientView = userState.permissions?.groups?.includes('client_user') || userState.permissions?.groups?.includes('client_admin');
  const { clientViews } = useLiveClientViews({ previewClientId });

  const [selection, setSelection] = useState<ReportMenuBarSelection>({
    source: '',
    accounts: [],
    campaigns: [],
    lineItems: [],
    dateFilter: '',
    startDate: '',
    endDate: '',
  } as ReportMenuBarSelection);
  const [formattedTimeSeriesData, setFormattedTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [videoCompletionRateData, setVideoCompletionRateData] = useState<{ percentage: number, percentChange: number }>({ percentage: 0, percentChange: 0 });
  const [audioCompletionRateData, setAudioCompletionRateData] = useState<{ percentage: number, percentChange: number }>({ percentage: 0, percentChange: 0 });

  const customizeContext = useReportViewCustomize(); // This is used to track client view customization
  const navigate = useNavigate();
  const [fetchReportMetrics, { data: metricsData, isLoading: isLoadingMetrics, error: metricsError }] = useGetReportMetricsMutation();
  const [fetchTimeSeriesData, { data: rawTimeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError }] = useGetTimeSeriesDataMutation();
  const [fetchGeoData, { data: geoData, isLoading: isLoadingGeoData, error: geoError }] = useGetGeoDataMutation();
  const [fetchHourlyData, { data: rawHourlyData, isLoading: isLoadingHourlyData, error: hourlyError }] = useGetHourlyDataMutation();
  const [fetchDomainData, { data: rawDomainData, isLoading: domainLoading, error: domainError }] = useGetDomainDataMutation();
  const [fetchAppData, { data: rawAppData, isLoading: appLoading, error: appError }] = useGetAppDataMutation();
  const [fetchBreakdownData, { data: rawPerformanceData, isLoading: isLoadingPerformanceData, error: performanceError }] = useGetBreakdownDataMutation();

  const getCurrentClientView = useCallback(() => {
    if (!selection.accounts.length) return FALLBACK_DEFAULT_CLIENT;

    // Helper function to find the appropriate view
    const findView = (views: ClientView[] | undefined) => {
      if (!views) return null;

      // If multiple accounts are selected, use client's default view
      if (selection.accounts.length > 1) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }

      // Find view for current account
      const currentView = views.find((view: ClientView) => view.account_ext_id === selection.accounts[0].id.toString());
      const notConfigured = currentView &&
        (!currentView.metrics?.length) &&
        (!currentView.graph_one?.metric_one && !currentView.graph_one?.metric_two) &&
        (!currentView.graph_two?.metric_one && !currentView.graph_two?.metric_two) &&
        (!currentView.graph_three?.metric_one && !currentView.graph_three?.metric_two);

      if (notConfigured) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }
      return currentView;
    };

    // If in preview mode, use the preview client's view configuration
    if (previewClientId) {
      return findView(clientViews) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
    }

    // Regular client view logic
    if (!isClientView) return FALLBACK_DEFAULT_CLIENT;
    return findView(clientViews) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
  }, [isClientView, selection.accounts, clientViews, previewClientId]);

  const shouldShowMetric = useCallback((metric: string) => {
    if (isClientView || previewClientId) {
      const currentView = getCurrentClientView();
      const included = currentView?.metrics.includes(metric) ?? false;
      if (!included) return false;
    }

    if (metric === 'geo') {
      return geoData?.length || 0 > 0 || isLoadingGeoData;
    }
    if (metric === 'domain') {
      return rawDomainData?.length || 0 > 0 || domainLoading;
    }
    if (metric === 'app') {
      return rawAppData?.length || 0 > 0 || appLoading;
    }
    if (metric === 'hourly') {
      return rawHourlyData?.length || 0 > 0 || isLoadingHourlyData;
    }

    if (!metricsData?.current) return true;
    if (metric === 'video_completion_rate') {
      return !!(metricsData?.current && metricsData.current.video_starts > 0);
    }
    if (metric === 'audio_completion_rate') {
      return !!(metricsData?.current && metricsData.current.audio_starts > 0);
    }
    const value = metricsData.current[metric];
    return typeof value !== 'undefined' && value !== 0;
  }, [isClientView, previewClientId, getCurrentClientView, metricsData, geoData, rawDomainData, rawAppData, rawHourlyData, isLoadingGeoData, domainLoading, appLoading, isLoadingHourlyData]);

  const clientPreviewId = previewClientId || undefined;

  useEffect(() => {
    if (selection.source && selection.accounts.length > 0 && selection.startDate && selection.endDate) {
      const reportParams = {
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
        previewClientId: clientPreviewId,
      };

      fetchReportMetrics(reportParams);
      fetchTimeSeriesData(reportParams);
      fetchGeoData(reportParams);
      fetchHourlyData(reportParams);
      fetchDomainData(reportParams);
      fetchAppData(reportParams);
      fetchBreakdownData(reportParams);
    }
  }, [selection, clientPreviewId]);

  useEffect(() => {
    setVideoCompletionRateData({
      percentage: calculateRate(metricsData?.current?.video_starts ?? 0, metricsData?.current?.video_completes ?? 0),
      percentChange: calculateRateDifference(metricsData?.current?.video_starts ?? 0, metricsData?.current?.video_completes ?? 0, metricsData?.dif?.video_starts ?? 0, metricsData?.dif?.video_completes ?? 0),
    });
    setAudioCompletionRateData({
      percentage: calculateRate(metricsData?.current?.audio_starts ?? 0, metricsData?.current?.audio_completes ?? 0),
      percentChange: calculateRateDifference(metricsData?.current?.audio_starts ?? 0, metricsData?.current?.audio_completes ?? 0, metricsData?.dif?.audio_starts ?? 0, metricsData?.dif?.audio_completes ?? 0),
    });
  }, [metricsData]);

  useEffect(() => {
    if (rawTimeSeriesData) {
      setFormattedTimeSeriesData(rawTimeSeriesData.map((point: any) => ({
        ...point,
        video_completion_rate: point.video_starts > 0 ? (point.video_completes / point.video_starts) * 100 : 0,
        audio_completion_rate: point.audio_starts > 0 ? (point.audio_completes / point.audio_starts) * 100 : 0,
      })));
    }

  }, [rawTimeSeriesData]);


  function handleHourClick(hour: number, metrics: HourlyMetrics): void {
    throw new Error('Function not implemented.');
  }

  const handleSelectionChange = (selection: ReportMenuBarSelection) => {
    setSelection(selection);
    onSelectionChange(selection);
  };

  return (
    <div className="h-full flex flex-col space-y-4 p-6 pt-0 gap-4 pb-10">
      <div className="flex-1 space-y-8 pb-[100px]">
        {showOldVersionButton && (
          <div className='relative'>
            <Button
              className='absolute right-0 -top-2'
              variant="outline"
              size="sm"
              onClick={() => navigate('/reports-old')}
            >
              <ArchiveRestore className="w-4 h-4" />
              Old Version
            </Button>
          </div>
        )}
        <ReportsMenuBar
          className='bg-gray-201 text-black rounded-full'
          onSelectionChange={handleSelectionChange}
          onDateChange={() => { }}
          onEditToggle={function(): void {
            throw new Error('Function not implemented.');
          } }
        />
        <div style={{ marginTop: '14px' }}>
          <ReportComponentWrapper
            componentType="metric-universal-display"
            maxDefaults={4}
            defaults={false}
          >
            <MetricUniversalDisplayCard
              metricsData={metricsData}
              isLoading={isLoadingMetrics || isLoadingTimeSeries}
              aggregateData={metricsData?.current || {impressions: 0, spend: 0, conversions: 0, clicks: 0}}
              timeSeriesData={formattedTimeSeriesData}
              startDate={selection.startDate}
              endDate={selection.endDate}
              handlePlatformClick={(metric, selectedMetricName) => handleMetricUniversalDisplayDefaultSelection(customizeContext as ReportViewCustomizeContextType, metric, selectedMetricName ?? '')}
              handleDownload={() => handleReportDownload(selection.source, selection.accounts.map(account => account.id), selection.campaigns.map(campaign => campaign.id), selection.startDate, selection.endDate)}
            />
          </ReportComponentWrapper>
        </div>


        <div className="flex justify-between w-full gap-8">
          <ReportComponentWrapper
            componentType="zipcode-heatmap"
            className='w-3/4'
            bottom={true}
            defaults={false}
          >
            <ZipcodeHeatmapDisplay
              className='w-3/4'
              data={geoData && geoData.length > 0 ? geoData as unknown as ZipcodeMetrics[] : []}
              config={{
                selectedMetric: 'impressions',
                showLegend: true,
                showTooltips: true,
                mapHeight: 400,
              }}
              onZipcodeClick={(zipcode) => console.log('Selected:', zipcode)}
            />
          </ReportComponentWrapper>
          <ReportComponentWrapper
            componentType="zipcode-rank"
            className='w-1/4'
            bottom={true}
            defaults={false}
          >
            <MetricSourceRank
              title="Zipcode"
              labelKey="Zipcode"
              onMetricChange={(metric) => handleComponentDefaultMetricSelection(customizeContext as ReportViewCustomizeContextType, 'zipcode-rank', metric)}
              data={geoData && geoData.length > 0 ? geoData as unknown as MetricRankDisplayData[] : []}
              className="w-1/4"
            />
          </ReportComponentWrapper>
        </div>
        <div>
          <ReportComponentWrapper
            componentType="time-of-day"
            className='w-full'
            defaults={false}
          >
            <TimeOfDayDisplay
              data={rawHourlyData ? transformHourlyData(rawHourlyData) : []}
              config={{
                selectedMetric: 'impressions',
                chartType: 'bar',
                showPeakHours: false,
                show24Hour: true,
                showGrid: true,
                showTooltips: true,
                chartHeight: 500,
                colorScheme: 'blue',
                highlightPeakHours: false,
                showAverageLines: true,
              }}
              onHourClick={handleHourClick}
              title="Hourly Performance"
              className="w-full"
            />
          </ReportComponentWrapper>
        </div>
        <div className="flex w-full gap-8">
          {/* omit if dv360 and data exists */}
          <ReportComponentWrapper
            componentType="domain-rank"
            className='w-1/3'
            defaults={false}
          >
            <MetricSourceRank
              title="Domain"
              labelKey="Domain"
              data={rawDomainData ? rawDomainData as unknown as MetricRankDisplayData[] : []}
              initialMetric="impressions"
              className="w-1/3"
            />
          </ReportComponentWrapper>

          <ReportComponentWrapper
            componentType="app-rank"
            className='w-1/3'
          >
            <MetricSourceRank
              title="App"
              labelKey="App"
              data={rawAppData ? rawAppData as unknown as MetricRankDisplayData[] : []}
              initialMetric="impressions"
              className="w-1/3"
            />
          </ReportComponentWrapper>

        </div>
        <div className="flex w-full gap-8">
          <ReportComponentWrapper
            componentType="video-completion-rate"
            fieldSelection={false}
            className='w-1/2'
          >
            <MetricPercentDisplay
              title="Video Completion Rate"
              percentage={videoCompletionRateData.percentage}
              percentChange={videoCompletionRateData.percentChange}
            />
          </ReportComponentWrapper>
          <ReportComponentWrapper
            componentType="audio-completion-rate"
            fieldSelection={false}
            className='w-1/2'
          >
            <MetricPercentDisplay
              title="Platform"
              percentage={audioCompletionRateData.percentage}
              percentChange={audioCompletionRateData.percentChange}
            />
          </ReportComponentWrapper>
        </div>
        <div className="flex w-full gap-8">
          <ReportComponentWrapper
            componentType="breakdown-table"
            fieldSelection={false}
            className='w-full'
          >
            <MetricBreakdownTable
              data={rawPerformanceData || []}
              isLoading={isLoadingPerformanceData}
              shouldShowMetric={shouldShowMetric as (metric: string) => boolean }
              handleDownload={() => handleReportDownload(selection.source, selection.accounts.map(account => account.id), selection.campaigns.map(campaign => campaign.id), selection.startDate, selection.endDate)}
            />
          </ReportComponentWrapper>
        </div>

      </div>
      <div className='mb-10' />
    </div>
  );
};

export default ReportsTab;